import { isWorkEmail } from 'utils/email-domains'
import { DotMatrix } from '@clickguard/design-system'

import { useAffiliateTracking, useNavigateWithParams, useStores } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { setItemsToLocalStorage } from '../../../../utils'
import { getHubspotUtk } from '../../../../utils/hubspot'
import { Onboarding } from '../../../../services/user'
import { parseError } from '../../../../utils/errors'
import { triggerLinkedInConversion } from '../../../../utils/triggers'
import { setDelay } from '../../../../utils/timeouts'
import {
	trackGetStarted2,
	trackPlansOrBookDemo3,
	trackBookingSchedule31,
	trackLeadFreeTrial
} from '../../../../utils/ga4-events'

import { WebsiteSpendStep } from './website-spend'
import { WebsiteSpendData } from './website-spend/types'
import { PlanPaymentPage } from './plan-payment'

import { BookDemoStep } from './book-demo'
import { OnboardingOfferLayout } from 'components/black-friday-banner/onboarding-offer-layout'

import { mapStepToV2, OnboardingStep } from './onboarding-step'

export const StartStandard = () => {
	const navigate = useNavigateWithParams()
	const { accountId } = useParams()
	const [searchParams, setSearchParams] = useSearchParams()
	useAffiliateTracking()

	const {
		accountStore,
		billingStore: { loadPlans },
		userStore: { user }
	} = useStores()
	const {
		setAccount,
		accounts,
		getAllAccounts,
		setAccountForId,
		getSubscriptionByAccountId
	} = accountStore

	const [step, setStep] = useState<OnboardingStep>('website-spend')
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		getAllAccounts()
		loadPlans()
	}, [accountId])

	const accountData = useMemo(
		() => accounts.find((a) => a.id === accountId),
		[accounts, accountId]
	)

	// Before workspace creation, the signup answer is the only agency signal.
	const isWorkspaceAgency = accountData
		? accountData.isAgency === true
		: user?.professional_role === 'Marketing_agency'

	useEffect(() => {
		if (!accountData) return

		const isAgency = accountData.isAgency === true
		const spend = Number(accountData.segmentation_ad_spend) || 0
		const setupStep = accountData.setup?.step as string | undefined

		const scheduledDemo = accountData.setup?.scheduled_demo ?? null
		const isNewWorkspace = accounts.some((a) => a.id !== accountData.id)
		const mappedStep = mapStepToV2(
			setupStep,
			isAgency,
			spend,
			scheduledDemo,
			isNewWorkspace,
			user?.is_personal_email ?? !isWorkEmail(user?.email || '')
		)
		if (mappedStep === 'book-demo' || mappedStep === 'plan-payment') {
			trackPlansOrBookDemo3()
		}
		const requested = searchParams.get('step')
		setStep(requested === 'plan-payment' ? 'plan-payment' : mappedStep)
		setAccount(accountData)

		if (setupStep === 'TRIAL_STARTED') {
			navigate(`/setup/${accountData.id}`)
		}
		if (setupStep === 'SETUP_COMPLETE') {
			navigate('/')
		}
	}, [accountData, accounts, searchParams, user?.is_personal_email])

	const handleWebsiteSpendSubmit = async (data: WebsiteSpendData) => {
		if (!data.websiteUrl) return
		setLoading(true)
		try {
			const hutk = getHubspotUtk()
			const account = await Onboarding.createProductAccount({
				type: 'standard',
				spend: data.adSpend * 1000,
				domain: data.websiteUrl.trim(),
				workspace_name: data.workspaceName?.trim() || undefined,
				step: 'workspace',
				hubspot_context: {
					hutk,
					page_name: document.title,
					page_uri: window.location.href
				}
			})
			setAccount(account)
			setItemsToLocalStorage([{ key: 'account_id', value: account?.id }])
			const spendValue = data.adSpend * 1000
			trackGetStarted2({
				website: data.websiteUrl.trim(),
				monthly_spend: spendValue,
				workspace_id: account.id
			})
			return navigate(`/start/standard/${account.id}`)
		} catch (err: any) {
			setLoading(false)
			toast.error(
				err?.response?.data?.message
					? parseError(err)
					: 'Something went wrong. Please try again.'
			)
		}
	}

	const handleDemoBooked = async (meetingDate?: string) => {
		trackBookingSchedule31(meetingDate ? { date: meetingDate } : undefined)
		if (accountId) {
			try {
				await Onboarding.updateSetup(accountId, { scheduled_demo: true })
			} catch {
				// Non-blocking
			}
		}
		setStep('plan-payment')
		setSearchParams(
			{ ...Object.fromEntries(searchParams), step: 'plan-payment' },
			{ replace: true }
		)
	}

	const handleDemoSkip = async () => {
		if (accountId) {
			try {
				await Onboarding.updateSetup(accountId, { scheduled_demo: false })
			} catch {
				// Non-blocking
			}
		}
		setStep('plan-payment')
		setSearchParams(
			{ ...Object.fromEntries(searchParams), step: 'plan-payment' },
			{ replace: true }
		)
	}

	// Show loading screen while account data is being fetched after navigation
	// This prevents the form from flashing before the step resolves
	if (accountId && !accountData) {
		return (
			<div className="min-h-screen w-full bg-gradient-to-br from-g-purple via-g-cyan to-g-gray flex items-center justify-center">
				<DotMatrix size="lg" className="!h-12 !w-12 !border-[5px]" />
			</div>
		)
	}

	if (step === 'website-spend') {
		return (
			<OnboardingOfferLayout accountId={accountId}>
				<WebsiteSpendStep
					onSubmit={handleWebsiteSpendSubmit}
					userEmail={user?.email}
					isAgency={isWorkspaceAgency}
					isNewWorkspace={!accountId && accounts.length !== 0}
					loading={loading}
				/>
			</OnboardingOfferLayout>
		)
	}

	if (step === 'book-demo') {
		return (
			<OnboardingOfferLayout accountId={accountId}>
				<BookDemoStep onDemoBooked={handleDemoBooked} onSkip={handleDemoSkip} />
			</OnboardingOfferLayout>
		)
	}

	if (step === 'plan-payment') {
		return (
			<PlanPaymentPage
				accountId={accountData?.id}
				isAgency={isWorkspaceAgency}
				initialAdSpend={Number(accountData?.setup?.spend) / 1000 || 30}
				initialPlanId={
					accountData?.setup?.selected_plan_details?.tier || undefined
				}
				onTrialActivated={async () => {
					// Allow the backend's trial activation to finish before loading the workspace.
					await setDelay(5000)
					triggerLinkedInConversion(user)
					await Promise.all([
						getAllAccounts(),
						setAccountForId(accountData!.id),
						setTimeout(() => getSubscriptionByAccountId(accountData!.id), 6000)
					])
					navigate(`/setup/${accountData!.id}`)
				}}
			/>
		)
	}

	return null
}
